import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { getConsent, respondConsent } from '../services/advisorNetworkService';

/**
 * Public page where a referred user reviews exactly what will be shared and
 * approves or declines. No login required — access is via a tokenized link.
 */

const Wrap = styled.div` min-height:100vh; background:#f3f4f6; display:flex; align-items:center; justify-content:center; padding:24px; font-family:-apple-system,Segoe UI,Roboto,sans-serif; `;
const Card = styled.div` background:#fff; border-radius:16px; box-shadow:0 10px 40px rgba(0,0,0,.1); max-width:520px; width:100%; overflow:hidden; `;
const Head = styled.div` background:linear-gradient(135deg,#8b5cf6,#7c3aed); color:#fff; padding:28px 28px 22px; text-align:center; `;
const Body = styled.div` padding:26px 28px 30px; color:#111827; `;
const H1 = styled.h1` font-size:20px; margin:8px 0 0; font-weight:700; `;
const P = styled.p` font-size:15px; line-height:1.7; color:#374151; margin:14px 0; `;
const Box = styled.div` background:#f9fafb; border-left:4px solid #0891b2; border-radius:12px; padding:18px 20px; margin:18px 0; `;
const BoxTitle = styled.div` color:#0891b2; font-weight:700; font-size:15px; margin-bottom:8px; `;
const Li = styled.div` color:#374151; font-size:14px; margin:6px 0; padding-left:18px; position:relative; &:before{content:'✓'; position:absolute; left:0; color:#059669; font-weight:700;} `;
const Preview = styled.div` background:#fff; border:1px solid #e5e7eb; border-radius:10px; padding:14px 16px; margin-top:12px; font-size:13px; color:#4b5563; white-space:pre-wrap; `;
const Btns = styled.div` display:flex; gap:12px; margin-top:24px; `;
const Approve = styled.button` flex:1; background:linear-gradient(135deg,#8b5cf6,#7c3aed); color:#fff; border:none; border-radius:10px; padding:14px; font-size:16px; font-weight:700; cursor:pointer; &:disabled{opacity:.6;} `;
const Decline = styled.button` flex:1; background:#fff; color:#6b7280; border:1px solid #d1d5db; border-radius:10px; padding:14px; font-size:16px; font-weight:600; cursor:pointer; &:disabled{opacity:.6;} `;
const Note = styled.p` font-size:12px; color:#9ca3af; text-align:center; margin-top:18px; `;
const Center = styled.div` text-align:center; padding:50px 20px; color:#6b7280; `;

export default function ReferralConsentPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // 'granted' | 'denied' | 'already'
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await getConsent(token);
      setData(d);
      if (d?.referral?.consentStatus && d.referral.consentStatus !== 'pending') setDone('already');
    } catch (e) {
      setError(e?.response?.data?.error || 'This link is invalid or has expired.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const respond = async (approve) => {
    try {
      setBusy(true);
      const res = await respondConsent(token, approve);
      setDone(res.consentStatus === 'granted' ? 'granted' : 'denied');
    } catch (e) {
      setError(e?.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  if (loading) return <Wrap><Card><Center>Loading…</Center></Card></Wrap>;
  if (error) return <Wrap><Card><Body><Center>{error}</Center></Body></Card></Wrap>;

  const r = data?.referral || {};
  const sd = data?.sharedData || {};

  if (done) {
    const msg = done === 'granted'
      ? `Thank you! We've shared your details with ${r.partnerName}. They'll reach out to you shortly.`
      : done === 'denied'
      ? 'No problem — nothing was shared. You can close this page.'
      : 'This referral has already been responded to.';
    return <Wrap><Card><Head><H1>{done==='granted'?'You’re all set ✅':'Done'}</H1></Head><Body><P style={{textAlign:'center'}}>{msg}</P></Body></Card></Wrap>;
  }

  return (
    <Wrap>
      <Card>
        <Head><div style={{fontSize:28}}>🔐</div><H1>Approve sharing your details</H1></Head>
        <Body>
          <P>Hello <strong>{r.userName || 'there'}</strong>,</P>
          <P><strong>{r.ownerName}</strong> would like to connect you with <strong>{r.partnerName}</strong> for <strong>{r.serviceLabel}</strong>, so you can get help with everything you need in one place.</P>

          <Box>
            <BoxTitle>What will be shared</BoxTitle>
            {(r.sharedItems || []).map((i, idx) => <Li key={idx}>{i}</Li>)}
            {(sd.summary || sd.notes || sd.contact) && (
              <Preview>
                {sd.contact?.name && `Name: ${sd.contact.name}\n`}
                {sd.contact?.email && `Email: ${sd.contact.email}\n`}
                {sd.contact?.phone && `Phone: ${sd.contact.phone}\n`}
                {sd.summary && `\nSummary: ${sd.summary}\n`}
                {sd.notes && `\nNote: ${sd.notes}`}
              </Preview>
            )}
            <P style={{margin:'12px 0 0', fontSize:13, color:'#6b7280'}}>Only this is shared — nothing else.</P>
          </Box>

          <Btns>
            <Decline onClick={()=>respond(false)} disabled={busy}>No thanks</Decline>
            <Approve onClick={()=>respond(true)} disabled={busy}>{busy?'…':'Approve & connect'}</Approve>
          </Btns>
          <Note>You can decline — it won't affect your relationship with {r.ownerName}.</Note>
        </Body>
      </Card>
    </Wrap>
  );
}
